// hooks/useCatalogues.js
import { useState, useCallback } from 'react';
import { App } from 'antd';
import { getUserCatalogues, getCatalogueStats } from '../catalogueService';
import { getSubscriptionNamesByCatalogueIdsForMyCatalogues } from '../utils/subscriptionUtils';
import catalogEvents, { CATALOG_EVENTS } from '../../utils/catalogEvents';

export const useCatalogues = () => {
    const { message: messageApi } = App.useApp();
    const [catalogues, setCatalogues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [catalogueStats, setCatalogueStats] = useState({});
    const [cataloguesCache, setCataloguesCache] = useState({
        published: [],
        draft: [],
        lastFetched: null
    });

    const fetchCatalogues = useCallback(async (catalogueListType, forceRefresh = false) => {
        try {
            const currentTime = Date.now();
            const cacheExpiry = 60000; // 1 minuto di cache
            const isCacheValid = cataloguesCache.lastFetched &&
                (currentTime - cataloguesCache.lastFetched < cacheExpiry) &&
                !forceRefresh;

            if (isCacheValid) {
                setCatalogues(cataloguesCache[catalogueListType]);
                return;
            }

            setLoading(true);
            const userCatalogues = await getUserCatalogues();

            // Ottieni i nomi delle subscriptions per tutti i cataloghi
            const catalogueIds = userCatalogues.map(catalogue => catalogue.id);
            const subscriptionNames = await getSubscriptionNamesByCatalogueIdsForMyCatalogues(catalogueIds);

            // Aggiungi il nome della subscription a ogni catalogo
            const cataloguesWithSubscriptions = userCatalogues.map(catalogue => ({
                ...catalogue,
                subscriptionName: subscriptionNames[catalogue.id] || '-'
            }));

            const publishedCatalogues = cataloguesWithSubscriptions.filter(catalogue =>
                !catalogue.isDraft && catalogue.status !== 'Cancelled'
            );
            const draftCatalogues = cataloguesWithSubscriptions.filter(catalogue => catalogue.isDraft);

            setCataloguesCache({
                published: publishedCatalogues,
                draft: draftCatalogues,
                lastFetched: currentTime
            });

            setCatalogues(catalogueListType === 'published' ? publishedCatalogues : draftCatalogues);

            const currentCatalogues = catalogueListType === 'published' ? publishedCatalogues : draftCatalogues;
            const statsPromises = currentCatalogues.map(async (catalogue) => {
                const stats = await getCatalogueStats(catalogue.id);
                return { catalogueId: catalogue.id, stats };
            });

            const allStats = await Promise.all(statsPromises);
            const statsMap = {};
            allStats.forEach(item => {
                statsMap[item.catalogueId] = item.stats;
            });

            setCatalogueStats(statsMap);

            // Emetti evento per notificare che i cataloghi sono stati aggiornati
            catalogEvents.emit(CATALOG_EVENTS.CATALOGS_REFRESHED, {
                catalogueListType,
                catalogues: currentCatalogues
            });
        } catch (error) {
            console.error("Error fetching catalogues:", error);
            messageApi.error("Failed to load catalogues");
        } finally {
            setLoading(false);
        }
    }, [messageApi, cataloguesCache]);

    return {
        catalogues,
        setCatalogues,
        loading,
        catalogueStats,
        cataloguesCache,
        fetchCatalogues
    };
};  